import React, { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import bem from 'utils/bem';
import Details from 'components/Details';
import Text from 'components/Text';
import ShopItemTeaser, { ShopItemTeaserSkeleton } from 'components/ShopItemTeaser';
import Page from '../../Page';
import Model from '../../../../model';
import { IconButton } from 'components/Button';
import MobileSlider from 'components/Slider/MobileSlider';
import { useMobile } from 'utils/media';
import TeaserPhoto from 'components/TeaserPhoto';
import { times } from 'utils/render';
import { Skeleton } from '@mui/material';
import './index.sass';
import { computed } from 'mobx';

const { block, el } = bem('shop-item');
const Digital = () => {
    const { name: slug } = useParams();

    const { digitalsSampleData, oneDigitalData } = Model.shop;

    const shopDigitalData = useMemo(() => oneDigitalData(slug), [ slug ]);
    const teasersData = digitalsSampleData([ slug ]);

    return (
        <Page className={ block() }>
            <Suspense fallback={ <ContentBlockSkeleton/> }>
                <ContentBlock resource={ shopDigitalData }/>
            </Suspense>
            <TeasersBlock slug={ slug } resource={ teasersData }/>
        </Page>
    )
}

const ContentBlock = ({ resource }) => {
    const shopItem = resource.read();

    const Photos = <TeaserPhoto src={ shopItem.data.photoResized || shopItem.data.photo }/>;

    return (
        <div className={ el('top-block') }>
            <div className={ el('photo-block') }>
                <div className={ el('photo') }>
                    { Photos }
                </div>
            </div>
            <div className={ el('description-block') }>
                <div className={ el('title') }>
                    <Text serif>{ shopItem.data.name }</Text>
                </div>
                <div className={ el('short-description') }>
                    <Text>{ shopItem.data.descriptionShort }</Text>
                </div>
                <div className={ el('full-description') }>
                    <Details>
                        <Text tag={ 'div' }>{ shopItem.data.descriptionFull }</Text>
                    </Details>
                </div>
                <div className={ el('separator') }/>
                <div className={ el('price-block') }>
                    <PriceBlock slug={ shopItem.data.url }/>
                </div>
            </div>
        </div>
    )
}

const PriceBlock = observer(({ slug }) => {
    const { getShopItemBySlug } = Model.shop;
    const { openCourseForm } = Model.modal;
    const shopItem = computed(() => {
        return getShopItemBySlug(slug)
    }).get();

    const openCourseFormWithProps = () => {
        openCourseForm({ price: shopItem.price, digitalId: shopItem.data.id, title: shopItem.data.name })
    }

    return (
        <>
            <div className={ el('price') }>
                <Text serif>{ shopItem.price } руб.</Text>
            </div>
            <div className={ el('buy-button') }>
                <IconButton name='shopping-cart' mod={ 'gold' } disabled={ !shopItem.isActive || shopItem.amount === 0 }
                            onClick = {openCourseFormWithProps}>
                    <Text>{ shopItem.buttonText }</Text>
                </IconButton>
            </div>
        </>
    )
})

const ContentBlockSkeleton = () => {
    const Photos = <TeaserPhoto/>;
    const Description = times((key) => <Skeleton key={ key }/>, 3);

    return (
        <div className={ el('top-block') }>
            <div className={ el('photo-block') }>
                { Photos }
            </div>
            <div className={ el('description-block') }>
                <div className={ el('title') }>
                    <Text serif><Skeleton/></Text>
                </div>
                <div className={ el('short-description') }>
                    <Text>{ Description }</Text>
                </div>
                <div className={ el('separator') }/>
                <div className={ el('price-block') }>
                    <div className={ el('price') }>
                        <Text serif><Skeleton width={ 83 }/></Text>
                    </div>
                    <div className={ el('cart-button') }>
                        <Skeleton width={ 173 } variant={ 'rounded' }/>
                    </div>
                </div>
            </div>
        </div>
    )
}


const TeasersBlock = memo(({ slug, resource }) => {
    const { isMobile } = useMobile();

    const SamplesFallback = isMobile ?
        <MobileSlider items={ times((key) => <ShopItemTeaserSkeleton key={ key }/>, 2) }/> :
        times((key) => <ShopItemTeaserSkeleton key={ key }/>, 4)

    return (
        <div className={ el('teasers-block') }>
            <div className={ el('teasers-block-title') }>
                <Text serif>Вас может заинтересовать</Text>
            </div>
            <div className={ el('teasers-block-list') }>
                <Suspense fallback={ SamplesFallback }>
                    <SamplesBlock resource={ resource } isMobile={ isMobile }/>
                </Suspense>
            </div>
        </div>
    )
})

const SamplesBlock = memo(({ resource, isMobile }) => {
    const samples = resource.read();
    const rSamples = samples.map((item, index) => <ShopItemTeaser key={ index } item={ item }/>);
    return isMobile ? <MobileSlider items={ rSamples }/> : rSamples
})

export default observer(Digital);
